import { SetStateAction, useContext, useEffect, useState } from "react"
import ChatCard from "../ChatCard"
import { AuthContext, AuthContextType } from "@/features/app-layout/context";
import { useGetThemes } from "../../api";
import { format, isToday } from "date-fns";
import ThemeCard from "../ThemeCard";
import { Button } from "@/shared/ui/button.tsx";
import ThemeCardLoader from "@/shared/loaders/ThemeCardLoader";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { ChatMessageType } from "../../types";
import { TextShimmer } from "@/shared/ui/text-shimmer.tsx";
import ThemeEmptyState from "./ThemeEmptyState";
import { useSearchParams } from "react-router-dom";

type Props = {
  setStep: (id: ChatMessageType) => void,
  setStatus: (id: number) => void,
  onThemeSelect: (id: number, nav: string) => void,
  onReasoning: (name: string, why: string) => void,
  date: Date,
  setDate: React.Dispatch<SetStateAction<Date | undefined>>
  status: number | undefined,
}

const ThemeSelection = ({ setStep, onReasoning, onThemeSelect, date, setDate, setStatus }: Props) => {

  const { name } = useContext<AuthContextType>(AuthContext)

  const [searchParams, setSearchParams] = useSearchParams()

  const currentPage = Number(searchParams.get("pn")) || 1;

  const [page, setPage] = useState(currentPage)

  useEffect(() => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("pn", String(page));
    setSearchParams(newParams);
  }, [page])

  const { themes: resultThemes } = useGetThemes(page, format(date, "yyyy-MM-dd"), searchParams.get("curr") === "campaign" ? 5 : 1)

  // const { pastThemes } = useGetPendingThemes(page, format(date, "yyyy-MM-dd"),  searchParams.get("curr") === "campaign" ? 5 : 1)

  // const resultThemes = searchParams.get("curr") === "campaign" ? themes : pastThemes

  return (
    <ChatCard user="assistant" key="first">
      <div className="w-full flex flex-col gap-2">
        <div className="font-header text-lg text-gray-700 font-semibold">Hello {name} 🤘</div>
        {resultThemes &&
          <p className="tracking-wider font-poppins text-[0.60rem] sm:text-xs leading-4 sm:leading-5 md:text-sm text-gray-500">{resultThemes.introduction}</p>
        }
        <p className="font-header text-gray-700 tracking-wider">🌴 Themes for {isToday(date) ? "Today" : format(date, "MMMM d")}</p>
        {!resultThemes && (
          <TextShimmer className='font-poppins text-sm tracking-wider font-medium' duration={5}>
            Generating theme...
          </TextShimmer>
        )}
        {resultThemes && resultThemes.data.length === 0 && (
          <ThemeEmptyState setStep={setStep} setStatus={setStatus} />
        )}
        <div className="w-full grid grid-cols-1 lg:grid-cols-2 md:grid-rows-3 gap-3">
          {!resultThemes && ([1, 2, 3, 4, 5].map((_, index) => (
            <ThemeCardLoader key={index} />
          )))}
          {resultThemes && resultThemes.data.map(item => (
            <ThemeCard to="result" data={item} key={item.event_id} onThemeSelect={onThemeSelect} onReasoning={onReasoning} />
          ))}
          {resultThemes && resultThemes.has_next_page &&
            <div
              onClick={() => {
                if (resultThemes?.data.length !== 0) {
                  setPage((prev) => prev + 1)
                }
              }}
              className="group relative cursor-pointer transition-all duration-500 ease-out w-full h-full"
            >
              <div className="p-[2px] cursor-pointer bg-gradient-to-tr h-full from-purple-400 via-indigo-500 to-pink-600 bg-[size:200%_200%] animate-gradient-xy rounded-3xl">
                <div className="relative rounded-3xl bg-white/95 backdrop-blur-xl h-full border border-white/20 shadow-2xl shadow-black/5 transition-all duration-500 ease-out group-hover:shadow-3xl group-hover:shadow-purple-500/10">
                  <div className="flex sm:items-center flex-col sm:flex-row justify-between p-4 gap-4 h-full">
                    <div className="flex items-center gap-4 flex-1">
                      {resultThemes.images.map((url, index) => (
                        <div
                          key={index}
                          className="relative group/image"
                          style={{
                            animationDelay: `${index * 150}ms`,
                          }}
                        >
                          <div className="relative size-14 md:size-20 rounded-lg md:rounded-2xl overflow-hidden bg-gray-50 shadow-lg shadow-black/10 transition-all duration-700 ease-out group-hover:scale-105 group-hover:shadow-xl group-hover:shadow-black/15 group/image:hover:scale-110 group/image:hover:z-10">
                            <img
                              src={url || "/placeholder.svg"}
                              alt={`Theme ${index + 1}`}
                              className="w-full h-full object-cover transition-all duration-700 ease-out group/image:hover:scale-105"
                            />

                            <div className="absolute inset-0 bg-gradient-to-t from-black/5 to-transparent opacity-0 transition-opacity duration-300 group/image:hover:opacity-100" />

                            <div className="absolute inset-0 rounded-2xl ring-1 ring-white/20 opacity-0 transition-opacity duration-300 group/image:hover:opacity-100" />
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="flex items-center gap-4 transition-all duration-300 ease-out">
                      <div className="text-right">
                        <p className="text-sm font-medium font-poppins text-gray-900 tracking-tight leading-tight transition-colors duration-300 group-hover:text-gray-800">
                          More themes
                        </p>
                        <p className="text-xs text-gray-500 font-normal font-poppins mt-0.5 transition-colors duration-300 group-hover:text-gray-600">
                          Explore collection
                        </p>
                      </div>

                      <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-100/80 transition-all duration-300 ease-out group-hover:bg-purple-100 group-hover:scale-110">
                        <ChevronRight
                          className={`w-4 h-4 text-gray-600 transition-all duration-300 ease-out group-hover:text-purple-600 group-hover:translate-x-0.5`}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="absolute inset-0 rounded-3xl shadow-inner shadow-black/5 pointer-events-none" />
                </div>

                <div className="absolute inset-0 rounded-3xl bg-gradient-to-r from-purple-500/20 via-violet-500/20 to-indigo-500/20 blur-xl opacity-0 transition-opacity duration-500 group-hover:opacity-30 -z-10 scale-105" />
              </div>
            </div>
          }
        </div>
        <div className="flex-1 h-32 flex items-end gap-2">
          {!isToday(date) &&
            <Button
              variant="outline"
              onClick={() => {
                setDate(new Date())
              }}
              className="flex-1 rounded-full font-poppins border-2 border-purple-400 hover:bg-white tracking-wide text-purple-600">
              <p className="hidden md:block">
                Today
              </p>
            </Button>
          }

          <Button
            // disabled={hasPrevPage}
            onClick={() => {
              if (page === 1) {
                setStep("navigation")
              }
              if (page !== 1) {
                setPage(prev => prev - 1)
              }
            }}
            variant="outline"
            className="flex-1 rounded-full font-poppins border-2 border-purple-400 hover:bg-white tracking-wide text-purple-600">
            <ChevronLeft className="block md:hidden" />
            <p className="hidden md:block">
              Back
            </p>
          </Button>
          <Button
            onClick={() => setStep("date")}
            variant="outline"
            className="flex-1 rounded-full font-poppins border-2 border-purple-400 hover:bg-white tracking-wide text-purple-600">
            Select Date
          </Button>
          {/*
            <Button
              onClick={() => setStep("navigation")}
              variant="outline"
              className="flex-1 rounded-full font-poppins border-2 border-purple-400 hover:bg-white tracking-wide text-purple-600 text-sm">
              Something else
            </Button>
          */}
        </div>
      </div>
    </ChatCard>
  )
}

export default ThemeSelection
